// Reusable GLSL chunks shared by the primitives.

/** HSV -> RGB for stable hue-based coloring. */
export const hsv2rgb = /* glsl */ `
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}
`

/** 3D simplex noise (Ashima Arts / Stefan Gustavson, public domain). */
export const snoise = /* glsl */ `
vec3 mod289(vec3 x){return x - floor(x * (1.0/289.0)) * 289.0;}
vec4 mod289(vec4 x){return x - floor(x * (1.0/289.0)) * 289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
float snoise(vec3 v){
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`

/**
 * Point sprite shaping: each point can be drawn as a circle / square / triangle,
 * and either as a soft translucent glow (default) or a hard opaque shape.
 * `pointMask()` returns the sprite coverage (0..1) at the current gl_PointCoord.
 * Used by the point-based primitives in their fragment shaders.
 */
export const POINT_SPRITE_GLSL = /* glsl */ `
  uniform float uPointShape;  // 0 circle, 1 square, 2 triangle
  uniform float uPointSolid;  // 0 soft glow, 1 hard opaque
  // Signed distance to an up-pointing equilateral triangle (<0 inside).
  float sdPointTri(vec2 p) {
    const float k = 1.7320508;
    float r = 0.9;
    p.y = -p.y;
    p.x = abs(p.x) - r;
    p.y = p.y + r / k;
    if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
    p.x -= clamp(p.x, -2.0 * r, 0.0);
    return -length(p) * sign(p.y);
  }
  float pointMask() {
    vec2 p = (gl_PointCoord - 0.5) * 2.0;   // -1..1
    bool solid = uPointSolid > 0.5;
    if (uPointShape < 0.5) {                 // circle
      float r = length(p);
      return solid ? 1.0 - smoothstep(0.94, 1.0, r) : pow(smoothstep(1.0, 0.0, r), 1.6);
    } else if (uPointShape < 1.5) {          // square
      float b = max(abs(p.x), abs(p.y));
      return solid ? 1.0 - smoothstep(0.92, 1.0, b) : pow(smoothstep(1.0, 0.0, b), 1.6);
    }
    float sd = sdPointTri(p);                // triangle
    return solid ? 1.0 - smoothstep(0.0, 0.06, sd) : 1.0 - smoothstep(-0.7, 0.06, sd);
  }
`

/**
 * Maximum number of Zone-reaction shapes alive at once (fixed array size for
 * WebGL1-safe uniform arrays + constant-bound loops).
 */
export const MAX_ZONES = 12

/**
 * Zone reaction — shared declarations + helpers used by both shader stages.
 * A "zone" is a shape that rides the primitive surface (parametrized by a unit
 * direction `dir = normalize(position)`) and, on its band's hit, locally
 * deforms the mesh underneath it.
 *
 * `zoneDisp(dir)` returns the outward displacement at a surface point. The loop
 * is constant-bound and uses masked accumulation (no break/continue on a
 * uniform) so it compiles on WebGL1; `uBandPulse` is read via a select, not a
 * dynamic index, for the same reason.
 */
export const ZONE_GLSL_COMMON = /* glsl */ `
  uniform float uZoneEnabled;
  uniform float uZoneCount;
  uniform vec3  uZoneCenter[${MAX_ZONES}];
  uniform float uZoneBand[${MAX_ZONES}];
  uniform float uZoneShape[${MAX_ZONES}];
  uniform float uZoneRadius[${MAX_ZONES}];
  uniform float uBandPulse[4];

  float zoneBandPulse(float band) {
    int b = int(band + 0.5);
    return uBandPulse[0] * float(b == 0) + uBandPulse[1] * float(b == 1)
         + uBandPulse[2] * float(b == 2) + uBandPulse[3] * float(b == 3);
  }

  // Maps a surface (u,v) in [0,1]^2 onto the unit sphere where the zones live,
  // so non-spherical primitives (tunnel, vortex, terrain, LED) can use the same
  // zone system: u -> longitude, v -> latitude.
  vec3 zoneDirFromUV(vec2 uv) {
    float lon = uv.x * 6.2831853;
    float lat = (clamp(uv.y, 0.0, 1.0) - 0.5) * 3.14159265;
    float cl = cos(lat);
    return vec3(cos(lon) * cl, sin(lat), sin(lon) * cl);
  }

  float zoneDisp(vec3 dir) {
    if (uZoneEnabled < 0.5) return 0.0;
    float total = 0.0;
    for (int i = 0; i < ${MAX_ZONES}; i++) {
      float zActive = step(float(i), uZoneCount - 0.5);
      float d = acos(clamp(dot(dir, uZoneCenter[i]), -1.0, 1.0));
      float r = max(uZoneRadius[i], 1e-3);
      float fall = smoothstep(r, 0.0, d);                 // 1 at center, 0 at edge
      total += zActive * fall * zoneBandPulse(uZoneBand[i]);
    }
    return total;
  }
`

/**
 * Zone reaction — fragment-only helpers. Include AFTER `hsv2rgb` and after
 * `ZONE_GLSL_COMMON`. `zoneColor(dir)` returns the overlay color+alpha of the
 * shape(s) under a surface point (circle / square / triangle / image).
 */
export const ZONE_GLSL_FRAG = /* glsl */ `
  uniform sampler2D uZoneImage0;
  uniform sampler2D uZoneImage1;
  uniform sampler2D uZoneImage2;
  uniform sampler2D uZoneImage3;
  uniform float uZoneImageActive[4];
  // 1 = draw the zone shapes, 0 = hide them (the deformation still happens).
  uniform float uZoneShowShapes;

  // Signed distance to an up-pointing equilateral triangle (iq), <0 inside.
  float zoneTri(vec2 p) {
    const float k = 1.7320508;
    float r = 0.8;
    p.x = abs(p.x) - r;
    p.y = p.y + r / k;
    if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
    p.x -= clamp(p.x, -2.0 * r, 0.0);
    return -length(p) * sign(p.y);
  }

  vec3 zoneSampleImage(float band, vec2 uv) {
    int b = int(band + 0.5);
    vec3 c = vec3(0.0);
    c += texture2D(uZoneImage0, uv).rgb * float(b == 0);
    c += texture2D(uZoneImage1, uv).rgb * float(b == 1);
    c += texture2D(uZoneImage2, uv).rgb * float(b == 2);
    c += texture2D(uZoneImage3, uv).rgb * float(b == 3);
    return c;
  }
  float zoneImageActive(float band) {
    int b = int(band + 0.5);
    return uZoneImageActive[0] * float(b == 0) + uZoneImageActive[1] * float(b == 1)
         + uZoneImageActive[2] * float(b == 2) + uZoneImageActive[3] * float(b == 3);
  }

  vec4 zoneColor(vec3 dir) {
    if (uZoneEnabled < 0.5 || uZoneShowShapes < 0.5) return vec4(0.0);
    vec3 acc = vec3(0.0);
    float aAcc = 0.0;
    for (int i = 0; i < ${MAX_ZONES}; i++) {
      float zActive = step(float(i), uZoneCount - 0.5);
      vec3 c = uZoneCenter[i];
      float r = max(uZoneRadius[i], 1e-3);
      float d = acos(clamp(dot(dir, c), -1.0, 1.0));
      // Local tangent-plane coords around the zone center, normalized by radius.
      vec3 upv = abs(c.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
      vec3 t = normalize(cross(upv, c));
      vec3 bt = cross(c, t);
      vec3 rel = dir - c * dot(dir, c);
      vec2 lp = vec2(dot(rel, t), dot(rel, bt)) / r;
      float shape = uZoneShape[i];
      float rad = length(lp);
      float m = 0.0;
      if (shape < 0.5) {                 // circle
        m = 1.0 - smoothstep(0.82, 1.0, rad);
      } else if (shape < 1.5) {          // square
        float box = max(abs(lp.x), abs(lp.y));
        m = 1.0 - smoothstep(0.72, 0.82, box);
      } else if (shape < 2.5) {          // triangle
        m = 1.0 - smoothstep(0.0, 0.06, zoneTri(lp));
      } else {                           // image (clipped to a soft disc)
        m = 1.0 - smoothstep(0.95, 1.0, rad);
      }
      m *= zActive * step(d, r * 1.3);
      float band = uZoneBand[i];
      vec3 col = hsv2rgb(vec3(fract(band * 0.27 + 0.05), 0.75, 1.0));
      if (shape > 2.5 && zoneImageActive(band) > 0.5) {
        col = zoneSampleImage(band, clamp(lp * 0.5 + 0.5, 0.0, 1.0));
      }
      float a = m * (0.45 + 0.55 * zoneBandPulse(band));
      acc = mix(acc, col, a);
      aAcc = max(aAcc, a);
    }
    return vec4(acc, aAcc);
  }
`
